import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from "react";
import { Image as ExpoImage } from "expo-image";
import * as Sentry from "@sentry/react-native";
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  onSnapshot,
  query,
  where
} from "firebase/firestore";
import { Platform } from "react-native";

import type { Dance, Performance, PerformanceDancerIndex, Recital, Studio } from "@recital-master/shared";
import { firestorePaths } from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import {
  DEV_MEDIA_BASE,
  loadDevCatalog,
  normalizeDance,
  performanceLeadArtworkKey,
  selectPerformanceFeaturedDances,
  type Catalog
} from "@/data/catalog";
import { sampleCatalog } from "@/data/sampleCatalog";
import { canCachePerformancePreparation } from "@/data/performancePreparation";
import { createBrowseArt } from "@/firebase/callables";
import { firestore, hasFirebaseClientConfig } from "@/firebase/client";
import { markImageDecoded } from "@/lib/imageReadiness";
import { clearPersistentArtwork, persistArtwork } from "@/lib/persistentArtwork";
import {
  getCachedPlaybackThumbUrl,
  getCachedPlaybackStoryboardVttUrl,
  subscribePlaybackGrantCache
} from "@/playback/playbackGrantCache";
import { usesProtectedPlayback } from "@/playback/playbackAccessMode";
import { useReadinessHold } from "@/providers/ReadinessGate";

type CatalogState = {
  status: "loading" | "ready" | "error";
  composed: boolean;
  catalog: Catalog | null;
  performanceDetailErrors: Record<string, true>;
  /** True when media is playable directly from the dev server. */
  devMedia: boolean;
  firebaseMode: boolean;
  /** Art-token base URL per granted recital (covers + thumbs, no video). */
  artBase: Record<string, string>;
  /** Stable local/cache URLs keyed by their canonical R2 object key. */
  artLocal: Record<string, string>;
  loadPerformanceDetails: (performanceId: string) => void;
  preparePerformance: (performanceId: string) => Promise<void>;
  prepareRecitalOverview: (recitalId: string) => Promise<void>;
};

type CatalogSessionState = CatalogState & {
  /** Identifies the authenticated data owner of this in-memory snapshot. */
  sessionKey: string | null;
  artExpiresAtMs: number | null;
};

type StateCommitWaiter = {
  isReady: (state: CatalogSessionState) => boolean;
  resolve: () => void;
};

const catalogSessions = new Map<string, CatalogSessionState>();
const ART_REFRESH_LEAD_MS = 5 * 60_000;
const ART_REFRESH_RETRY_MS = 60_000;
const LIVE_LISTENER_RETRY_DELAYS_MS = [1_000, 3_000, 10_000, 30_000] as const;

function isRetryableFirestoreListenerError(error: unknown): boolean {
  const code =
    typeof error === "object" && error != null && "code" in error
      ? String(error.code).replace(/^firestore\//, "")
      : null;
  return (
    code == null ||
    code === "aborted" ||
    code === "cancelled" ||
    code === "deadline-exceeded" ||
    code === "internal" ||
    code === "resource-exhausted" ||
    code === "unauthenticated" ||
    code === "unavailable" ||
    code === "unknown"
  );
}

function catalogSessionKey(firebaseMode: boolean, uid: string | undefined): string | null {
  if (!firebaseMode) return "demo";
  return uid ?? null;
}

function usableCatalogSession(session: CatalogSessionState | undefined): CatalogSessionState | null {
  if (!session || session.status !== "ready" || !session.composed) return null;
  if (!session.firebaseMode) return session;
  return session.artExpiresAtMs != null && session.artExpiresAtMs > Date.now() + 60_000
    ? session
    : null;
}

const CatalogContext = createContext<CatalogState>({
  status: "loading",
  composed: false,
  catalog: null,
  performanceDetailErrors: {},
  devMedia: false,
  firebaseMode: false,
  artBase: {},
  artLocal: {},
  loadPerformanceDetails: () => {},
  preparePerformance: async () => {},
  prepareRecitalOverview: async () => {}
});

/** Art URL for any object key under a granted recital, else null. */
function artUrlFor(artBase: Record<string, string>, objectKey: string): string | null {
  const recitalId = /^studios\/[^/]+\/recitals\/([^/]+)\//.exec(objectKey)?.[1];
  const base = recitalId ? artBase[recitalId] : null;
  return base ? `${base}/${objectKey}` : null;
}

function mediaUrlFor(
  objectKey: string | null | undefined,
  devMedia: boolean,
  artBase: Record<string, string>,
  artLocal: Record<string, string> = {}
): string | null {
  if (!objectKey) return null;
  if (artLocal[objectKey]) return artLocal[objectKey];
  return devMedia ? `${DEV_MEDIA_BASE}/media/${objectKey}` : artUrlFor(artBase, objectKey);
}

function browseArtworkKeys(catalog: Catalog): string[] {
  return [
    ...catalog.recitals.map((recital) => recital.coverImagePath),
    ...catalog.recitals.map((recital) => recital.intro?.thumbPath),
    ...Object.values(catalog.dances).flatMap((dances) =>
      dances.map((dance) => dance.thumbPath)
    )
  ].filter((key): key is string => Boolean(key));
}

async function persistArtworkKeys(
  uid: string,
  objectKeys: string[],
  artBase: Record<string, string>
): Promise<Record<string, string>> {
  // The browser HTTP cache already gives web a stable decoded asset without
  // copying every signed image through Expo FileSystem. Safari's IndexedDB-
  // backed file path is materially slower and must not sit on the launch
  // curtain's critical path. Native keeps the durable local artwork cache.
  if (Platform.OS === "web") return {};
  const entries = await Promise.all(
    [...new Set(objectKeys)].map(async (objectKey) => {
      const remoteUrl = artUrlFor(artBase, objectKey);
      if (!remoteUrl) return null;
      try {
        return [objectKey, await persistArtwork(uid, objectKey, remoteUrl)] as const;
      } catch {
        return null;
      }
    })
  );
  return Object.fromEntries(entries.filter((entry): entry is readonly [string, string] => entry != null));
}

function browseCoverUrls(
  catalog: Catalog,
  devMedia: boolean,
  artBase: Record<string, string>,
  artLocal: Record<string, string> = {}
): string[] {
  return catalog.recitals
    .map((recital) => {
      const firstPerformance = catalog.performances.find((performance) => performance.recitalId === recital.recitalId);
      const featuredKey = firstPerformance
        ? performanceLeadArtworkKey(firstPerformance, catalog.dances[firstPerformance.performanceId] ?? [])
        : null;
      return mediaUrlFor(
        recital.coverImagePath ?? featuredKey,
        devMedia,
        artBase,
        artLocal
      );
    })
    .filter((url): url is string => Boolean(url));
}

function prefetchImage(url: string): Promise<void> {
  // On web, wait for decode as well as download. React Native Web can remount
  // an already-cached image during browser history restoration and deliver
  // onLoad one frame later; recording decoded URLs lets that remount paint at
  // its settled opacity immediately instead of exposing its placeholder.
  if (typeof window !== "undefined" && typeof window.Image === "function") {
    return new Promise((resolve) => {
      const image = new window.Image();
      image.onload = () => {
        const decoded =
          typeof image.decode === "function"
            ? image.decode().catch(() => undefined)
            : Promise.resolve();
        void decoded.then(() => {
          markImageDecoded(url);
          resolve();
        });
      };
      image.onerror = () => resolve();
      image.src = url;
    });
  }
  // Use the same memory+disk cache as the mounted Artwork view. A successful
  // preparation is therefore a real rendering contract: the destination can
  // mount the sharp image immediately instead of replaying its blur/fallback.
  return ExpoImage.prefetch(url, { cachePolicy: "memory-disk" })
    .then((ready) => {
      if (ready) markImageDecoded(url);
    });
}

function prefetchImages(urls: string[]): Promise<void> {
  return Promise.allSettled([...new Set(urls)].map(prefetchImage)).then(() => undefined);
}

async function loadFirestoreCatalog(uid: string): Promise<Catalog> {
  if (!firestore) throw new Error("Firestore is not configured.");

  // Account validity and grant membership are independent reads. Starting
  // them together removes one full network round trip from every cold boot.
  const [account, grants] = await Promise.all([
    getDocFromServer(doc(firestore, firestorePaths.familyAccount(uid))),
    getDocsFromServer(collection(firestore, `familyAccounts/${uid}/grants`))
  ]);
  const accessExpiresAtMs = Number(account.get("accessExpiresAtMs"));
  if (
    !account.exists() ||
    !Number.isFinite(accessExpiresAtMs) ||
    accessExpiresAtMs <= Date.now()
  ) {
    return {
      studios: [],
      recitals: [],
      performances: [],
      dances: {},
      dancerIndexes: {}
    };
  }

  const activeGrantIds = grants.docs
    .filter((entry) => entry.get("revokedAt") == null)
    .map((entry) => entry.id);

  const recitalDocs = await Promise.all(
    activeGrantIds.map((recitalId) => getDoc(doc(firestore!, firestorePaths.recital(recitalId))))
  );
  const recitals = recitalDocs
    .filter((entry) => entry.exists())
    .map((entry) => {
      const recital = entry.data() as Recital;
      if (!recital.studioSlug) {
        throw new Error(`Recital ${entry.id} is missing its canonical studioSlug.`);
      }
      const intro = entry.get("intro") as Dance | null | undefined;
      return {
        ...recital,
        recitalId: entry.id,
        artColors: entry.get("artColors") ?? [],
        intro: intro ? normalizeDance(intro) : null
      };
    })
    .sort((a, b) => b.year - a.year);

  const studioIds = [...new Set(recitals.map((recital) => recital.studioId))];
  // Once recital membership is known, performance and studio metadata can
  // load concurrently rather than adding another serial Firestore phase.
  const [performancesByRecital, studioSnapshots] = await Promise.all([
    Promise.all(
      recitals.map(async (recital) => {
        // Rules require published==true to be asserted by the query itself —
        // security rules are not filters.
        const performances = await getDocs(
          query(
            collection(firestore!, `recitals/${recital.recitalId}/performances`),
            where("published", "==", true)
          )
        );
        return performances.docs
          .map((entry) => ({
            ...(entry.data() as Performance),
            performanceId: entry.id,
            recitalId: recital.recitalId
          }))
          .sort((a, b) => a.eventDate.localeCompare(b.eventDate) || a.title.localeCompare(b.title));
      })
    ),
    Promise.all(
      studioIds.map((studioId) =>
        getDoc(doc(firestore!, firestorePaths.studio(studioId)))
      )
    )
  ]);

  const performances = performancesByRecital.flat();
  const studios = studioIds.map((studioId, index) => {
    const snapshot = studioSnapshots[index];
    if (snapshot?.exists()) {
      return { ...(snapshot.data() as Studio), studioId: snapshot.id } as Studio;
    }
    return {
      studioId,
      name:
        performances.find((performance) => performance.studioId === studioId)?.studioName ??
        studioId,
      slug: studioId
    };
  });
  return { studios, recitals, performances, dances: {}, dancerIndexes: {} };
}

/**
 * Loads the catalog. Dev order: public synthetic fixture catalog.json
 * (playable media), else the bundled sample catalog (design-time, no media).
 * A Firestore-backed source replaces this behind the same context once
 * signed playback lands.
 */
export function CatalogProvider({ children }: PropsWithChildren) {
  const {
    user,
    demoMode,
    authBootstrapComplete,
    loading: authLoading
  } = useAuthState();
  const firestoreCatalogMode = usesProtectedPlayback({
    firebaseConfigured: hasFirebaseClientConfig,
    demoMode
  });
  const sessionKey = catalogSessionKey(firestoreCatalogMode, user?.uid);
  const loadCycleRef = useRef(0);
  const [loadCycle, setLoadCycle] = useState(0);
  const warmedCycleRef = useRef(0);
  const previousSessionKeyRef = useRef(sessionKey);
  const [state, setState] = useState<CatalogSessionState>(() => {
    const restored = sessionKey
      ? usableCatalogSession(catalogSessions.get(sessionKey))
      : null;
    return restored ?? {
      status: "loading",
      composed: false,
      catalog: null,
      performanceDetailErrors: {},
      devMedia: false,
      firebaseMode: firestoreCatalogMode,
      artBase: {},
      artLocal: {},
      loadPerformanceDetails: () => {},
      preparePerformance: async () => {},
      prepareRecitalOverview: async () => {},
      sessionKey,
      artExpiresAtMs: null
    };
  });
  const committedStateRef = useRef(state);
  const stateCommitWaitersRef = useRef<StateCommitWaiter[]>([]);
  const performanceDetailPromisesRef = useRef<Map<string, Promise<Dance[]>>>(new Map());
  const performancePreparationPromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  const performanceArtworkWarmPromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  const grantRefreshRef = useRef(0);

  // Completing Firestore/filesystem work does not mean React has published
  // it. Navigation preparation waits through this layout-phase boundary so a
  // destination's first render reads the committed snapshot.
  useLayoutEffect(() => {
    committedStateRef.current = state;
    const pending: StateCommitWaiter[] = [];
    for (const waiter of stateCommitWaitersRef.current) {
      if (waiter.isReady(state)) waiter.resolve();
      else pending.push(waiter);
    }
    stateCommitWaitersRef.current = pending;
  }, [state]);

  const waitForStateCommit = useCallback(
    (isReady: StateCommitWaiter["isReady"]): Promise<void> => {
      if (isReady(committedStateRef.current)) return Promise.resolve();
      return new Promise((resolve) => {
        stateCommitWaitersRef.current.push({ isReady, resolve });
      });
    },
    []
  );

  useEffect(() => {
    const previous = previousSessionKeyRef.current;
    if (previous && previous !== sessionKey) {
      catalogSessions.delete(previous);
      performancePreparationPromisesRef.current.clear();
      performanceArtworkWarmPromisesRef.current.clear();
    }
    previousSessionKeyRef.current = sessionKey;
  }, [sessionKey]);

  // Expo's browser history restore can reconstruct the root route tree. Keep
  // the already-composed session outside that tree so the restored screen's
  // first render has the same catalog and signed art bases as its last one.
  useEffect(() => {
    if (sessionKey && state.sessionKey === sessionKey && state.status === "ready") {
      catalogSessions.set(sessionKey, state);
    }
  }, [sessionKey, state]);

  // Browse-art tokens outlive an ordinary session but not an app left open
  // overnight. Refresh them before expiry without rebuilding the catalog.
  // Playback grants renew independently, so failing to do this leaves video
  // working while covers and thumbnails fall back to their blur placeholders.
  useEffect(() => {
    if (
      !user ||
      !state.firebaseMode ||
      state.status !== "ready" ||
      !state.catalog ||
      state.artExpiresAtMs == null
    ) {
      return undefined;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let refreshPending = false;
    const catalog = state.catalog;

    const schedule = (delayMs: number) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void refresh();
      }, Math.max(0, delayMs));
    };

    const refresh = async () => {
      if (refreshPending) return;
      refreshPending = true;
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
      try {
        const art = await createBrowseArt();
        const artBase = Object.fromEntries(
          art.grants.map((grant) => [grant.recitalId, grant.artBaseUrl])
        );
        const artLocal = await persistArtworkKeys(
          user.uid,
          browseArtworkKeys(catalog),
          artBase
        );
        await prefetchImages(browseCoverUrls(catalog, false, artBase, artLocal));
        if (cancelled) return;

        const expirations = art.grants
          .map((grant) => Date.parse(grant.expiresAt))
          .filter(Number.isFinite);
        setState((current) => ({
          ...current,
          artBase,
          artLocal: { ...current.artLocal, ...artLocal },
          artExpiresAtMs:
            expirations.length > 0 ? Math.min(...expirations) : Date.now() + 5 * 60_000
        }));
      } catch {
        if (!cancelled) schedule(ART_REFRESH_RETRY_MS);
      } finally {
        refreshPending = false;
      }
    };

    // Browsers can freeze a background tab for hours and resume it after the
    // timer's deadline. Re-check on every foreground lifecycle signal so the
    // page swaps in fresh bearer URLs instead of remounting expired ones and
    // producing a wall of 403s before the delayed timer gets CPU time again.
    const refreshIfNeeded = () => {
      if (state.artExpiresAtMs! <= Date.now() + ART_REFRESH_LEAD_MS) {
        void refresh();
      }
    };
    const refreshWhenVisible = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        refreshIfNeeded();
      }
    };

    schedule(state.artExpiresAtMs - Date.now() - ART_REFRESH_LEAD_MS);
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.addEventListener("focus", refreshIfNeeded);
      window.addEventListener("online", refreshIfNeeded);
      window.addEventListener("pageshow", refreshIfNeeded);
      document.addEventListener("visibilitychange", refreshWhenVisible);
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.removeEventListener("focus", refreshIfNeeded);
        window.removeEventListener("online", refreshIfNeeded);
        window.removeEventListener("pageshow", refreshIfNeeded);
        document.removeEventListener("visibilitychange", refreshWhenVisible);
      }
    };
  }, [state.artExpiresAtMs, state.catalog, state.firebaseMode, state.status, user]);

  useEffect(() => {
    const restored = sessionKey
      ? usableCatalogSession(catalogSessions.get(sessionKey))
      : null;
    if (restored) {
      setState((current) => (current === restored ? current : restored));
      return undefined;
    }

    let cancelled = false;
    const cycle = loadCycleRef.current + 1;
    loadCycleRef.current = cycle;
    setLoadCycle(cycle);
    const composeCurrentCatalog = async (
      catalog: Catalog,
      devMedia: boolean,
      artBasePromise: Promise<Record<string, string>>
    ) => {
      const artBase = await artBasePromise;
      await prefetchImages(browseCoverUrls(catalog, devMedia, artBase));
      if (!cancelled && loadCycleRef.current === cycle) {
        setState((current) => ({ ...current, composed: true }));
      }
    };

    (async () => {
      try {
        if (firestoreCatalogMode) {
          // Wait for claimAccess/activateDevice so a first sign-in sees its
          // freshly-claimed grants instead of an empty library.
          if (!user || !authBootstrapComplete) {
            if (!cancelled) {
              setState((current) => ({
                ...current,
                sessionKey,
                status: "loading",
                composed: false,
                catalog: null,
                performanceDetailErrors: {},
                devMedia: false,
                firebaseMode: true,
                artBase: {},
                artLocal: {},
                artExpiresAtMs: null
              }));
            }
            return;
          }
          setState((current) => ({
            ...current,
            sessionKey,
            status: "loading",
            composed: false,
            catalog: null,
            performanceDetailErrors: {},
            devMedia: false,
            firebaseMode: true,
            artBase: {},
            artLocal: {},
            artExpiresAtMs: null
          }));
          const firestoreCatalog = await loadFirestoreCatalog(user.uid);
          const catalog = firestoreCatalog;
          if (!cancelled) {
            setState((current) => ({
              ...current,
              status: "ready",
              composed: false,
              catalog,
              devMedia: false,
              firebaseMode: true
            }));
          }
          // A cold start is not composed until browse art tokens and the
          // first-screen covers have actually settled. Cached sessions still
          // bypass this work during SPA history restoration.
          const artBasePromise = createBrowseArt()
            .then(async (art) => {
              const artBase = Object.fromEntries(
                art.grants.map((grant) => [grant.recitalId, grant.artBaseUrl])
              );
              const artLocal = await persistArtworkKeys(
                user.uid,
                browseArtworkKeys(catalog),
                artBase
              );
              if (!cancelled && loadCycleRef.current === cycle) {
                const expirations = art.grants
                  .map((grant) => Date.parse(grant.expiresAt))
                  .filter(Number.isFinite);
                const artExpiresAtMs = expirations.length > 0
                  ? Math.min(...expirations)
                  : Date.now() + 5 * 60_000;
                setState((current) => ({
                  ...current,
                  artBase,
                  artLocal: { ...current.artLocal, ...artLocal },
                  artExpiresAtMs
                }));
              }
              return artBase;
            })
            .catch(() => ({}));
          void composeCurrentCatalog(catalog, false, artBasePromise);
          return;
        }

        setState((current) => ({
          ...current,
          sessionKey,
          status: "loading",
          composed: false,
          catalog: null,
          performanceDetailErrors: {},
          devMedia: false,
          firebaseMode: false,
          artBase: {},
          artLocal: {},
          artExpiresAtMs: null
        }));
        const catalog = await loadDevCatalog();
        if (!cancelled) {
          setState((current) => ({
            ...current,
            status: "ready",
            composed: false,
            catalog,
            devMedia: true
          }));
          void composeCurrentCatalog(catalog, true, Promise.resolve({}));
        }
      } catch (error) {
        console.error("[catalog] initial load failed", error);
        Sentry.captureException(error, {
          tags: { bootstrap: "catalog" }
        });
        if (!cancelled && !firestoreCatalogMode) {
          const catalog = sampleCatalog;
          setState((current) => ({
            ...current,
            status: "ready",
            composed: false,
            catalog: sampleCatalog,
            devMedia: false
          }));
          void composeCurrentCatalog(catalog, false, Promise.resolve({}));
        } else if (!cancelled) {
          setState((current) => ({ ...current, status: "error", composed: true }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authBootstrapComplete, firestoreCatalogMode, sessionKey, user]);

  // Grants are mutable account state, but the catalog must still swap as one
  // prepared snapshot. Listen only to grant membership; when it changes,
  // rebuild and prefetch off-screen, then atomically replace the catalog.
  useEffect(() => {
    if (
      !state.firebaseMode ||
      !firestore ||
      !user ||
      !authBootstrapComplete ||
      state.status !== "ready" ||
      !state.composed ||
      !state.catalog
    ) {
      return;
    }

    const activeFirestore = firestore;
    let cancelled = false;
    let listenerFailed = false;
    let listenerRetryAttempt = 0;
    let listenerRetryTimer: ReturnType<typeof setTimeout> | undefined;
    let unsubscribe: (() => void) | undefined;

    const clearListenerRetry = () => {
      if (listenerRetryTimer) clearTimeout(listenerRetryTimer);
      listenerRetryTimer = undefined;
    };
    const scheduleListenerRetry = () => {
      if (cancelled || listenerRetryTimer) return;
      const attempt = Math.min(
        listenerRetryAttempt,
        LIVE_LISTENER_RETRY_DELAYS_MS.length - 1
      );
      listenerRetryAttempt += 1;
      listenerRetryTimer = setTimeout(() => {
        listenerRetryTimer = undefined;
        startListener();
      }, LIVE_LISTENER_RETRY_DELAYS_MS[attempt]);
    };
    const startListener = () => {
      if (cancelled) return;
      clearListenerRetry();
      unsubscribe?.();
      listenerFailed = false;
      unsubscribe = onSnapshot(
        collection(activeFirestore, `familyAccounts/${user.uid}/grants`),
        (snapshot) => {
          listenerRetryAttempt = 0;
          const activeGrantIds = snapshot.docs
            .filter((entry) => entry.get("revokedAt") == null)
            .map((entry) => entry.id)
            .sort();
          const visibleRecitalIds = state.catalog!.recitals
            .map((recital) => recital.recitalId)
            .sort();
          if (activeGrantIds.join("\n") === visibleRecitalIds.join("\n")) return;

          const refresh = grantRefreshRef.current + 1;
          grantRefreshRef.current = refresh;
          void (async () => {
            // A revoked recital must not survive in this account's durable
            // artwork directory. Rebuild the small cache from active grants.
            await clearPersistentArtwork(user.uid);
            const [catalog, art] = await Promise.all([
              loadFirestoreCatalog(user.uid),
              createBrowseArt()
            ]);
            const artBase = Object.fromEntries(
              art.grants.map((grant) => [grant.recitalId, grant.artBaseUrl])
            );
            const artLocal = await persistArtworkKeys(
              user.uid,
              browseArtworkKeys(catalog),
              artBase
            );
            await prefetchImages(browseCoverUrls(catalog, false, artBase, artLocal));
            if (cancelled || grantRefreshRef.current !== refresh) return;

            const expirations = art.grants
              .map((grant) => Date.parse(grant.expiresAt))
              .filter(Number.isFinite);
            setState((current) => {
              const allowedPerformances = new Set(
                catalog.performances.map((performance) => performance.performanceId)
              );
              const retainedDances = Object.fromEntries(
                Object.entries(current.catalog?.dances ?? {}).filter(([performanceId]) =>
                  allowedPerformances.has(performanceId)
                )
              );
              const retainedDancerIndexes = Object.fromEntries(
                Object.entries(current.catalog?.dancerIndexes ?? {}).filter(([performanceId]) =>
                  allowedPerformances.has(performanceId)
                )
              );
              return {
                ...current,
                catalog: {
                  ...catalog,
                  dances: retainedDances,
                  dancerIndexes: retainedDancerIndexes
                },
                artBase,
                artLocal,
                artExpiresAtMs:
                  expirations.length > 0 ? Math.min(...expirations) : Date.now() + 5 * 60_000,
                status: "ready",
                composed: true
              };
            });
          })().catch(() => {
            // Keep the fully composed current catalog if a live refresh fails.
          });
        },
        (error) => {
          // Firestore's error callback is terminal for this listener. Keep the
          // fully composed catalog on screen, then attach a new listener once
          // the suspended tab's network transport is usable again.
          unsubscribe = undefined;
          listenerFailed = isRetryableFirestoreListenerError(error);
          if (listenerFailed) scheduleListenerRetry();
        }
      );
    };
    const retryListenerNow = () => {
      if (!listenerFailed) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      startListener();
    };
    const retryListenerWhenVisible = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        retryListenerNow();
      }
    };

    startListener();
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.addEventListener("focus", retryListenerNow);
      window.addEventListener("online", retryListenerNow);
      window.addEventListener("pageshow", retryListenerNow);
      document.addEventListener("visibilitychange", retryListenerWhenVisible);
    }
    return () => {
      cancelled = true;
      clearListenerRetry();
      unsubscribe?.();
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.removeEventListener("focus", retryListenerNow);
        window.removeEventListener("online", retryListenerNow);
        window.removeEventListener("pageshow", retryListenerNow);
        document.removeEventListener("visibilitychange", retryListenerWhenVisible);
      }
    };
  }, [authBootstrapComplete, state.catalog, state.composed, state.firebaseMode, state.status, user]);

  const fetchPerformanceDetails = useCallback(async (performance: Performance): Promise<Dance[]> => {
    if (!firestore) return [];
    const [dancesSnapshot, dancerIndexSnapshot] = await Promise.all([
      getDocs(collection(firestore, `recitals/${performance.recitalId}/performances/${performance.performanceId}/dances`)),
      getDoc(doc(firestore, firestorePaths.dancerIndex(performance.recitalId, performance.performanceId)))
    ]);
    const dances = dancesSnapshot.docs
      .filter((entry) => entry.get("contentKind") !== "recital-intro")
      .map((entry) =>
        normalizeDance({ ...(entry.data() as Dance), danceId: entry.id })
      )
      .sort((a, b) => a.danceNumber - b.danceNumber);
    const dancerIndex = dancerIndexSnapshot.exists()
      ? ({ ...(dancerIndexSnapshot.data() as PerformanceDancerIndex), performanceId: performance.performanceId } as PerformanceDancerIndex)
      : null;

    setState((current) => {
      if (!current.catalog) return current;
      const { [performance.performanceId]: _resolvedError, ...performanceDetailErrors } =
        current.performanceDetailErrors;
      return {
        ...current,
        performanceDetailErrors,
        catalog: {
          ...current.catalog,
          dances: { ...current.catalog.dances, [performance.performanceId]: dances },
          dancerIndexes: dancerIndex
            ? { ...current.catalog.dancerIndexes, [performance.performanceId]: dancerIndex }
            : current.catalog.dancerIndexes
        }
      };
    });
    await waitForStateCommit(
      (current) => current.catalog?.dances[performance.performanceId] === dances
    );
    return dances;
  }, [waitForStateCommit]);

  const resolvePerformanceDetails = useCallback(async (
    performanceId: string
  ): Promise<{ dances: Dance[]; performance: Performance } | null> => {
    if (!performanceId || !state.catalog) return null;
    const performance = state.catalog.performances.find((entry) => entry.performanceId === performanceId);
    if (!performance) return null;

    let dances = state.catalog.dances[performanceId];
    if (!dances && firestoreCatalogMode && firestore) {
      let pending = performanceDetailPromisesRef.current.get(performanceId);
      if (!pending) {
        pending = fetchPerformanceDetails(performance);
        performanceDetailPromisesRef.current.set(performanceId, pending);
      }
      try {
        dances = await pending;
      } catch (error) {
        setState((current) => ({
          ...current,
          performanceDetailErrors: { ...current.performanceDetailErrors, [performanceId]: true }
        }));
        throw error;
      } finally {
        if (performanceDetailPromisesRef.current.get(performanceId) === pending) {
          performanceDetailPromisesRef.current.delete(performanceId);
        }
      }
    }

    return { dances: dances ?? [], performance };
  }, [fetchPerformanceDetails, firestoreCatalogMode, state.catalog]);

  const prepareArtworkKeys = useCallback(async (
    requestedKeys: Array<string | null | undefined>
  ): Promise<void> => {
    const snapshot = committedStateRef.current;
    if (!snapshot.catalog) return;
    const artworkKeys = [...new Set(requestedKeys.filter(
      (key): key is string => Boolean(key)
    ))];
    const newlyLocal = user && !snapshot.devMedia
      ? await persistArtworkKeys(user.uid, artworkKeys, snapshot.artBase)
      : {};
    const latestArtLocal = committedStateRef.current.artLocal;
    const artLocal = { ...latestArtLocal, ...newlyLocal };
    const changedLocal = Object.fromEntries(
      Object.entries(newlyLocal).filter(([key, uri]) => latestArtLocal[key] !== uri)
    );
    await prefetchImages(
      artworkKeys
        .map((key) => mediaUrlFor(key, snapshot.devMedia, snapshot.artBase, artLocal))
        .filter((url): url is string => Boolean(url))
    );
    const changedEntries = Object.entries(changedLocal);
    if (changedEntries.length === 0) return;
    setState((current) => ({
      ...current,
      artLocal: { ...current.artLocal, ...changedLocal }
    }));
    await waitForStateCommit(
      (current) => changedEntries.every(([key, uri]) => current.artLocal[key] === uri)
    );
  }, [user, waitForStateCommit]);

  const preparePerformanceArtwork = useCallback((
    performance: Performance,
    dances: Dance[]
  ): Promise<void> => {
    const catalog = committedStateRef.current.catalog;
    if (!catalog) return Promise.resolve();
    const existing = performanceArtworkWarmPromisesRef.current.get(performance.performanceId);
    if (existing) return existing;
    const coverKey = catalog.recitals.find(
      (recital) => recital.recitalId === performance.recitalId
    )?.coverImagePath;
    const featuredKeys = selectPerformanceFeaturedDances(performance, dances).map(
      (dance) => dance.thumbPath
    );

    const criticalKeys = [coverKey, ...featuredKeys];
    const criticalKeySet = new Set(criticalKeys.filter(Boolean));
    const remainingKeys = dances
      .map((dance) => dance.thumbPath)
      .filter((key) => !criticalKeySet.has(key));

    // Warm the destination's lead imagery first, then its entire dance list
    // into memory+disk. Navigation callers await this same deduplicated task,
    // while the library-wide idle warmer normally completes it before a tap.
    // That keeps a fast first navigation coherent without replaying thumbnail
    // placeholder transitions on the destination route.
    const pending = prepareArtworkKeys(criticalKeys)
      .then(() => prepareArtworkKeys(remainingKeys));
    performanceArtworkWarmPromisesRef.current.set(performance.performanceId, pending);
    void pending.catch(() => {
      if (performanceArtworkWarmPromisesRef.current.get(performance.performanceId) === pending) {
        performanceArtworkWarmPromisesRef.current.delete(performance.performanceId);
      }
    });
    return pending;
  }, [prepareArtworkKeys]);

  const preparePerformance = useCallback((performanceId: string): Promise<void> => {
    // A cold deep link can request details before the catalog has committed.
    // Do not cache that no-op: the route's effect must retry when the catalog
    // arrives and publishes a new preparation callback.
    if (!canCachePerformancePreparation(state.catalog, performanceId)) {
      return Promise.resolve();
    }
    const preparationKey = `${loadCycle}:${performanceId}`;
    const existing = performancePreparationPromisesRef.current.get(preparationKey);
    if (existing) return existing;

    const pending = (async () => {
      const resolved = await resolvePerformanceDetails(performanceId);
      if (!resolved) return;
      await preparePerformanceArtwork(resolved.performance, resolved.dances);
    })().catch((error) => {
      if (performancePreparationPromisesRef.current.get(preparationKey) === pending) {
        performancePreparationPromisesRef.current.delete(preparationKey);
      }
      throw error;
    });
    performancePreparationPromisesRef.current.set(preparationKey, pending);
    return pending;
  }, [loadCycle, preparePerformanceArtwork, resolvePerformanceDetails, state.catalog]);

  /**
   * Prepare exactly what the recital overview paints: complete dance metadata
   * for every Performance card, plus each card's three featured stills. The broader
   * performance-thumbnail warmer remains opportunistic, but visible fan artwork is
   * part of the navigation readiness contract.
   */
  const prepareRecitalOverview = useCallback(async (
    recitalId: string
  ): Promise<void> => {
    if (!state.catalog || !recitalId) return;
    const performances = state.catalog.performances.filter(
      (performance) => performance.recitalId === recitalId
    );
    const settledPerformances = await Promise.allSettled(
      performances.map((performance) => resolvePerformanceDetails(performance.performanceId))
    );
    const resolvedPerformances = settledPerformances.flatMap((result) =>
      result.status === "fulfilled" && result.value ? [result.value] : []
    );
    const recital = state.catalog.recitals.find(
      (recital) => recital.recitalId === recitalId
    );
    await prepareArtworkKeys([
      recital?.coverImagePath,
      recital?.intro?.thumbPath,
      ...resolvedPerformances.flatMap(({ performance, dances }) =>
        selectPerformanceFeaturedDances(performance, dances).map((dance) => dance.thumbPath)
      )
    ]);
  }, [prepareArtworkKeys, resolvePerformanceDetails, state.catalog]);

  const loadPerformanceDetails = useCallback((performanceId: string) => {
    void preparePerformance(performanceId).catch(() => {});
  }, [preparePerformance]);

  // During Firebase's first auth callback `user` is briefly null even when a
  // persisted signed-in session is about to be restored. Do not treat that
  // transient state as a signed-out, ready catalog.
  const catalogReady = firestoreCatalogMode
    ? (!authLoading && !user) || state.composed
    : state.composed;
  useReadinessHold("catalog", catalogReady);

  useEffect(() => {
    if (!state.composed || state.status !== "ready" || !state.catalog) return;
    if (warmedCycleRef.current === loadCycle) return;
    warmedCycleRef.current = loadCycle;
    const catalog = state.catalog;
    void (async () => {
      try {
        await prefetchImages(browseCoverUrls(catalog, false, state.artBase, state.artLocal));
        await Promise.all(catalog.performances.map((performance) => preparePerformance(performance.performanceId)));
      } catch {
        // Idle warm-up is opportunistic only.
      }
    })();
    return undefined;
  }, [loadCycle, preparePerformance, state.artBase, state.artLocal, state.catalog, state.composed, state.status]);

  const value = useMemo<CatalogState>(
    () => ({ ...state, loadPerformanceDetails, preparePerformance, prepareRecitalOverview }),
    [loadPerformanceDetails, preparePerformance, prepareRecitalOverview, state]
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog(): CatalogState {
  return useContext(CatalogContext);
}

export function useRecitals(): Recital[] {
  return useCatalog().catalog?.recitals ?? [];
}

export function useRecital(recitalId: string | undefined): Recital | null {
  const { catalog } = useCatalog();
  return catalog?.recitals.find((r) => r.recitalId === recitalId) ?? null;
}

export function useStudio(studioId: string | undefined): Studio | null {
  const { catalog } = useCatalog();
  return catalog?.studios.find((studio) => studio.studioId === studioId) ?? null;
}

/** Performances, optionally scoped to a recital, in catalog order. */
export function usePerformances(recitalId?: string): Performance[] {
  const { catalog } = useCatalog();
  return useMemo(() => {
    const all = catalog?.performances ?? [];
    return recitalId ? all.filter((performance) => performance.recitalId === recitalId) : all;
  }, [catalog, recitalId]);
}

export function usePerformance(performanceId: string | undefined): Performance | null {
  const { catalog } = useCatalog();
  return catalog?.performances.find((performance) => performance.performanceId === performanceId) ?? null;
}

export function useDances(performanceId: string | undefined): Dance[] {
  const { catalog, loadPerformanceDetails } = useCatalog();
  useEffect(() => {
    if (performanceId) loadPerformanceDetails(performanceId);
  }, [loadPerformanceDetails, performanceId]);
  return useMemo(
    () => (performanceId ? catalog?.dances[performanceId] ?? [] : []),
    [catalog, performanceId]
  );
}

export function usePerformanceDetailsStatus(
  performanceId: string | undefined
): "loading" | "ready" | "error" {
  const { catalog, firebaseMode, performanceDetailErrors, status } = useCatalog();
  if (!performanceId) return "loading";
  if (performanceDetailErrors[performanceId]) return "error";
  if (catalog?.dances[performanceId]) return "ready";
  if (!firebaseMode && status === "ready") return "ready";
  return "loading";
}

export function useDancerIndex(performanceId: string | undefined): PerformanceDancerIndex | null {
  const { catalog, loadPerformanceDetails } = useCatalog();
  useEffect(() => {
    if (performanceId) loadPerformanceDetails(performanceId);
  }, [loadPerformanceDetails, performanceId]);
  return performanceId ? catalog?.dancerIndexes[performanceId] ?? null : null;
}

/** Dev-only direct media URL for an R2 object key. */
export function useDevMediaUrl(): (objectKey: string) => string {
  return (objectKey: string) => `${DEV_MEDIA_BASE}/media/${objectKey}`;
}

/**
 * Resolves an R2 object key to a fetchable URL, or null when media isn't
 * reachable (bundled sample catalog). Signed playback sessions will replace
 * this for production media.
 */
export function useMediaUrl(): (objectKey: string | null | undefined) => string | null {
  const { catalog, devMedia, firebaseMode, artBase, artLocal } = useCatalog();
  const [, bump] = useState(0);
  useEffect(
    () => subscribePlaybackGrantCache(() => bump((current) => current + 1)),
    []
  );
  return (objectKey) => {
    if (devMedia && objectKey) return `${DEV_MEDIA_BASE}/media/${objectKey}`;
    if (!firebaseMode || !objectKey || !catalog) return null;

    const art = artLocal[objectKey] ?? artUrlFor(artBase, objectKey);
    if (art) return art;

    // Fallback while art tokens are in flight: an active playback session's
    // signed thumb URL, when this key is one of its dances.
    for (const dances of Object.values(catalog.dances)) {
      const dance = dances.find((entry) => entry.thumbPath === objectKey);
      if (dance) return getCachedPlaybackThumbUrl(dance.danceId);
    }
    const intro = catalog.recitals.find((entry) => entry.intro?.thumbPath === objectKey)?.intro;
    if (intro) return getCachedPlaybackThumbUrl(intro.danceId);
    for (const dances of Object.values(catalog.dances)) {
      const dance = dances.find((entry) => entry.storyboardVttPath === objectKey);
      if (dance) return getCachedPlaybackStoryboardVttUrl(dance.danceId);
    }
    return null;
  };
}

/** The operator-selected dance still that leads a performance's fan and card mood. */
export function usePerformanceFeaturedArtwork(performanceId: string | undefined): string | null {
  const { catalog, devMedia, artBase, artLocal } = useCatalog();
  if (!catalog || !performanceId) return null;
  const performance = catalog.performances.find((entry) => entry.performanceId === performanceId);
  const dances = catalog.dances[performanceId] ?? [];
  const key = performance ? performanceLeadArtworkKey(performance, dances) : null;
  if (!key) return null;
  return devMedia
    ? `${DEV_MEDIA_BASE}/media/${key}`
    : artLocal[key] ?? artUrlFor(artBase, key);
}

/** A recital's program cover, falling back to its first featured dance still. */
export function useRecitalArtwork(recitalId: string | undefined): string | null {
  const { catalog, devMedia, artBase, artLocal } = useCatalog();
  const recital = catalog?.recitals.find((entry) => entry.recitalId === recitalId);
  const firstPerformance = catalog?.performances.find((performance) => performance.recitalId === recitalId);
  const firstPerformanceArt = usePerformanceFeaturedArtwork(firstPerformance?.performanceId);
  if (!catalog || !recitalId) return null;
  if (recital?.coverImagePath) {
    return devMedia
      ? `${DEV_MEDIA_BASE}/media/${recital.coverImagePath}`
      : artLocal[recital.coverImagePath] ?? artUrlFor(artBase, recital.coverImagePath);
  }
  return firstPerformanceArt;
}
